import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

// Customer artwork on the submission form (#626). Shows the field only for
// decorated services, checks each file before uploading it straight to
// storage, and keeps one hidden signed-id input per finished upload. The
// server checks everything again on submit.
export default class extends Controller {
  static targets = [
    "section",
    "help",
    "input",
    "list",
    "template",
    "errors",
  ];

  static values = {
    url: String,
    extensions: Array,
    heicExtensions: Array,
    maxBytes: Number,
    maxFiles: Number,
    messages: Object,
  };

  connect() {
    this.uploading = 0;
    this.form = this.element.closest("form");
    this.guardSubmit = this.guardSubmit.bind(this);
    this.form?.addEventListener("submit", this.guardSubmit);
    this.toggle();
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.guardSubmit);
  }

  toggle() {
    const service = this.element.querySelector(
      'input[name="order_submission[service]"]:checked',
    )?.value;
    const decorated = service === "screen_print" || service === "embroidery";

    this.sectionTarget.classList.toggle("d-none", !decorated);
    this.helpTargets.forEach((help) => {
      help.classList.toggle("d-none", help.dataset.service !== service);
    });
    // Hidden for blanks: don't post signed ids.
    this.sectionTarget.querySelectorAll("input").forEach((input) => {
      if (input !== this.inputTarget) input.disabled = !decorated;
    });
  }

  select() {
    const files = Array.from(this.inputTarget.files);
    this.inputTarget.value = "";
    this.clearErrors();
    files.forEach((file) => this.add(file));
  }

  remove(event) {
    const item = event.target.closest("li");
    item.removed = true;
    item.xhr?.abort();
    // An aborted PUT never calls DirectUpload back (BlobUpload only listens
    // for load and error), so settle it here.
    this.settle(item);
    item.remove();
  }

  add(file) {
    const problem = this.problemWith(file);
    if (problem) {
      this.showError(problem);
      return;
    }

    const item = this.templateTarget.content.firstElementChild.cloneNode(true);
    item.querySelector("[data-name]").textContent = file.name;
    this.listTarget.appendChild(item);
    this.upload(file, item);
  }

  upload(file, item) {
    this.uploading += 1;
    item.pending = true;
    const bar = item.querySelector(".progress-bar");
    const upload = new DirectUpload(file, this.urlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        item.xhr = xhr;
        // Removed while its URL was being issued: cancel once the PUT
        // starts (aborting before send would make send throw).
        if (item.removed) {
          xhr.addEventListener("loadstart", () => xhr.abort());
        }
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            bar.style.width = `${(event.loaded / event.total) * 100}%`;
          }
        });
      },
    });

    upload.create((error, blob) => {
      this.settle(item);
      if (item.removed) return;

      if (error) {
        item.remove();
        this.showError(this.message("upload_failed", file.name));
        return;
      }

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "order_submission[artwork_files][]";
      input.value = blob.signed_id;
      item.appendChild(input);
      item.querySelector("[data-progress]").remove();
    });
  }

  // Counts an upload as finished exactly once, however it ends.
  settle(item) {
    if (!item.pending) return;
    item.pending = false;
    item.xhr = null;
    this.uploading -= 1;
  }

  guardSubmit(event) {
    if (this.uploading > 0) {
      event.preventDefault();
      this.clearErrors();
      this.showError(this.messagesValue.wait_for_uploads);
    }
  }

  problemWith(file) {
    const extension = file.name.includes(".")
      ? file.name.split(".").pop().toLowerCase()
      : "";

    if (this.listTarget.children.length >= this.maxFilesValue) {
      return this.messagesValue.count;
    }
    if (this.heicExtensionsValue.includes(extension)) {
      return this.messagesValue.heic;
    }
    if (!this.extensionsValue.includes(extension)) {
      return this.message("type", file.name);
    }
    if (file.size > this.maxBytesValue) {
      return this.message("size", file.name);
    }
    if (file.size === 0) return this.message("empty", file.name);

    return null;
  }

  message(key, filename) {
    return this.messagesValue[key].replace("%{filename}", filename);
  }

  showError(message) {
    const line = document.createElement("div");
    line.textContent = message;
    this.errorsTarget.appendChild(line);
  }

  clearErrors() {
    this.errorsTarget.replaceChildren();
  }
}
